import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { classNames } from "../../helpers";
import { useNavigate } from "react-router-dom";
import { useNavigationHelpers } from "../../hooks/useNavigationHelpers";

interface CustomVideoRecorderProps {
  onRecordingComplete: (blobUrl: string) => void;
  enableVideo: boolean;
  onError: (error: string) => void;
  isUploading: boolean;
  mediaBlobUrl: string | null;
}
// eslint-disable-next-line react-hooks/rules-of-hooks

const VideoRecorder: React.FC<CustomVideoRecorderProps> = ({
  onRecordingComplete,
  enableVideo,
  onError,
  isUploading,
  mediaBlobUrl,
}) => {
  const { t } = useTranslation("", { keyPrefix: "page.readingRecorder" });
  const [isRecording, setIsRecording] = useState(false);
  const [isPreviewActive, setIsPreviewActive] = useState(false);
  const [showRecordedVideo, setShowRecordedVideo] = useState(false);
  const [recordedVideoUrl, setRecordedVideoUrl] = useState<string | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string>("");
  const [selectedMic, setSelectedMic] = useState<string>("");
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [recordingTime, setRecordingTime] = useState(0);
  const startTimeRef = useRef<number>(0);
  const timerRef = useRef<number>();
  const navigate = useNavigate();
  const { t: button } = useTranslation("", {
    keyPrefix: "page.readingRecorder",
  });

  const { handleBack } = useNavigationHelpers();
  // Function to stop all media tracks
  const stopAllMediaTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsPreviewActive(false);
    setIsRecording(false);
  };

  // Request permissions when component mounts
  useEffect(() => {
    const requestPermissions = async () => {
      try {
        // Request camera and microphone permissions
        const stream = await navigator.mediaDevices.getUserMedia({
          video: enableVideo,
          audio: true,
        });

        // Stop the stream immediately after getting permissions
        stream.getTracks().forEach((track) => track.stop());

        setPermissionsGranted(true);
        // After permissions are granted, get the devices
        getDevices();
      } catch (error) {
        console.error("Permission error:", error);
        setPermissionError(t("microphoneCameraRequired"));
      }
    };

    requestPermissions();

    // Add beforeunload event listener
    const handleBeforeUnload = () => {
      stopAllMediaTracks();
      if (recordedVideoUrl) {
        URL.revokeObjectURL(recordedVideoUrl);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup function to stop all media tracks when component unmounts
    return () => {
      stopAllMediaTracks();
      if (recordedVideoUrl) {
        URL.revokeObjectURL(recordedVideoUrl);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      // Remove the beforeunload event listener
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [onError, enableVideo]);

  // Get available devices
  const getDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setDevices(devices);

      // Set default camera and microphone
      const cameras = devices.filter(
        (d) => d.kind === "videoinput" && d.deviceId
      );
      const mics = devices.filter((d) => d.kind === "audioinput" && d.deviceId);

      const defaultCamera = cameras[0]?.deviceId || "";
      const defaultMic = mics[0]?.deviceId || "";

      setSelectedCamera(defaultCamera);
      setSelectedMic(defaultMic);

      // Start preview automatically with default devices
      if (defaultCamera || defaultMic) {
        startPreview(defaultCamera, defaultMic);
      }
    } catch (error) {
      console.error("Error enumerating devices:", error);
      onError("Failed to access media devices");
    }
  };

  const startPreview = async (cameraId?: string, micId?: string) => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: enableVideo
          ? {
              deviceId: cameraId ? { exact: cameraId } : undefined,
              facingMode: "user",
              width: { ideal: 640 }, // Lowered resolution
              height: { ideal: 360 },
              frameRate: { ideal: 15, max: 20 }, // Lowered frame rate
            }
          : false,
        audio: {
          deviceId: micId ? { exact: micId } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setIsPreviewActive(true);
    } catch (error) {
      console.error("Error starting preview:", error);
      onError(
        "Failed to start preview. Please check your camera and microphone permissions."
      );
    }
  };

  const stopPreview = () => {
    stopAllMediaTracks();
  };

  const startRecording = async () => {
    if (!streamRef.current) {
      await startPreview(selectedCamera, selectedMic);
    }

    try {
      const mediaRecorder = new MediaRecorder(streamRef.current!, {
        mimeType: "video/webm;codecs=vp9,opus",
        videoBitsPerSecond: 1000000, // 1 Mbps
        audioBitsPerSecond: 128000, // 128 kbps
      });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: "video/webm;codecs=vp9,opus",
        });
        const blobUrl = URL.createObjectURL(blob);
        setRecordedVideoUrl(blobUrl);
        setShowRecordedVideo(true);
        // Ensure all chunks are processed
        chunksRef.current = [];
      };

      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
    } catch (error) {
      console.error("Error starting recording:", error);
      // Fallback to default codec if VP9 is not supported
      try {
        const mediaRecorder = new MediaRecorder(streamRef.current!, {
          mimeType: "video/webm;codecs=vp8,opus",
          videoBitsPerSecond: 1000000,
          audioBitsPerSecond: 128000,
        });
        mediaRecorderRef.current = mediaRecorder;
        chunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          const blob = new Blob(chunksRef.current, {
            type: "video/webm;codecs=vp8,opus",
          });
          const blobUrl = URL.createObjectURL(blob);
          setRecordedVideoUrl(blobUrl);
          setShowRecordedVideo(true);
          chunksRef.current = [];
        };

        mediaRecorder.start(1000);
        setIsRecording(true);
      } catch (fallbackError) {
        console.error("Error with fallback codec:", fallbackError);
        onError(
          "Failed to start recording. Please check your browser support."
        );
      }
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    stopAllMediaTracks();
  };

  const handleCameraChange = async (deviceId: string) => {
    setSelectedCamera(deviceId);
    await startPreview(deviceId, selectedMic);
  };

  const handleMicChange = async (deviceId: string) => {
    setSelectedMic(deviceId);
    await startPreview(selectedCamera, deviceId);
  };

  const handleReRecord = () => {
    if (recordedVideoUrl) {
      URL.revokeObjectURL(recordedVideoUrl);
    }
    setRecordedVideoUrl(null);
    setShowRecordedVideo(false);
    startPreview(selectedCamera, selectedMic);
  };

  const handleSubmit = () => {
    if (recordedVideoUrl) {
      onRecordingComplete(recordedVideoUrl);
    }
  };

  // Handle recording timer
  useEffect(() => {
    if (isRecording) {
      startTimeRef.current = Date.now();
      setRecordingTime(0);

      timerRef.current = window.setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setRecordingTime(elapsed);

        // Auto-stop after 5 minutes (300 seconds)
        if (elapsed >= 300) {
          stopRecording();
        }
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording]);

  return (
    <div className="flex flex-col items-center justify-between w-full gap-4">
      {permissionError ? (
        <div className="w-full max-w-2xl p-4 mb-4 text-red-800 bg-red-100 rounded-lg">
          <p>{permissionError}</p>
          <button
            onClick={() => {
              setPermissionError(null);
              navigator.mediaDevices
                .getUserMedia({ video: enableVideo, audio: true })
                .then((stream) => {
                  stream.getTracks().forEach((track) => track.stop());
                  setPermissionsGranted(true);
                  getDevices();
                })
                .catch((error) => {
                  console.error("Permission error:", error);
                  setPermissionError(
                    "Camera and microphone permissions ar-1e required for recording"
                  );
                });
            }}
            className="px-4 py-2 mt-2 text-white bg-red-600 rounded"
          >
            {t("retryPermissions")}
          </button>
        </div>
      ) : (
        <div>
          {!isRecording && !showRecordedVideo && (
            <div className="flex flex-col w-full max-w-2xl gap-4 sm:flex-row">
              {enableVideo && (
                <select
                  dir="ltr"
                  className="w-full p-2 border rounded pe-10"
                  onChange={(e) => handleCameraChange(e.target.value)}
                  value={selectedCamera}
                >
                  {devices
                    .filter((d) => d.kind === "videoinput")
                    .map((camera) => (
                      <option key={camera.deviceId} value={camera.deviceId}>
                        {camera.label || "Camera"}
                      </option>
                    ))}
                </select>
              )}
              <select
                dir="ltr"
                className="w-full p-2 border rounded pe-10"
                onChange={(e) => handleMicChange(e.target.value)}
                value={selectedMic}
              >
                {devices
                  .filter((d) => d.kind === "audioinput")
                  .map((mic) => (
                    <option key={mic.deviceId} value={mic.deviceId}>
                      {mic.label || "Microphone"}
                    </option>
                  ))}
              </select>
            </div>
          )}
          <div className="relative w-full max-w-2xl pt-2">
            {showRecordedVideo ? (
              <div className="flex flex-col items-center gap-4">
                <video
                  src={recordedVideoUrl || ""}
                  controls
                  className="w-full transform rounded-lg "
                />
              </div>
            ) : (
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="w-full transform rounded-lg"
              />
            )}
          </div>
        </div>
      )}
      <div
        className={classNames(
          "z-20 px-10 py-4 mt-10 w-full md:px-0  sticky  items-center flex-wrap border-t border-gray-200 bottom-0 bg-white gap-3 flex justify-between"
        )}
      >
        {/* {mediaBlobUrl && (
          <div className="flex flex-col items-end justify-end w-full gap-4 md:flex-row md:items-center">
            <button
              className={classNames(
                " bg-brandblue-600 hover:bg-brandblue-700 focus:outline-none focus:ring-none disabled:bg-gray-400 whitespace-nowrap",
                "flex justify-center flex-shrink-0 px-6 py-2 md:px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white"
              )}
              onClick={onSubmit}
              disabled={isUploading}
            >
              {isUploading ? t("uploading...") : t("sendRegistration")}
            </button>
          </div>
        )} */}
        <button
          onClick={handleBack}
          className="px-6 py-2 text-sm text-gray-700 border-2 border-gray-300 rounded-md whitespace-nowrap md:px-4"
        >
          {button("back")}
        </button>
        {!isRecording && !recordedVideoUrl && permissionsGranted && (
          <div className="flex justify-between ">
            <button
              onClick={() => startRecording()}
              className="px-4 py-2 text-sm text-white rounded-md bg-brandblue-600 whitespace-nowrap"
            >
              {t("recordingStart")}
            </button>
          </div>
        )}

        {(isRecording || recordedVideoUrl) && (
          <div className="flex items-center gap-2 p-2 bg-opacity-50 rounded w-fit ">
            {/* <div className="relative w-4 h-4">
                <div className="absolute inset-0 bg-red-500 rounded-full opacity-75 animate-ping"></div>
                <div className="absolute inset-0 bg-red-500 rounded-full"></div>
              </div> */}
            <span className="text-gray-500 text-[32px] font-semibold">
              {formatTime(recordingTime)}
            </span>
          </div>
        )}

        {isRecording && (
          <button
            onClick={stopRecording}
            className="px-4 py-2 text-sm text-white bg-red-600 rounded-md whitespace-nowrap"
          >
            {t("stopRegistration")}
          </button>
        )}

        {!isRecording && recordedVideoUrl && (
          <div className="flex gap-4 justify-between w-full sm:w-auto">
            <button
              onClick={handleReRecord}
              className="px-4 py-2 text-sm text-white bg-gray-500 rounded-md whitespace-nowrap"
            >
              {t("recordRecord")}
            </button>

            <button
              onClick={handleSubmit}
              className="px-4 py-2 text-sm text-white rounded-md bg-brandblue-600 whitespace-nowrap"
              disabled={isUploading}
            >
              {isUploading ? t("uploading...") : t("submit")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoRecorder;
